import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import ListPageHeader from '@shared/ListPageHeader';
import TableIndex from '@shared/table/TableIndex';
import type { TableService, SupportRule } from '@shared/table/TableIndex';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import adminApiFetch from '@services/admin/apiService';
import FullDayRangePicker from '@components/shared/FullDayRangePicker';
import { MultipleValue } from '@shared/table/Rules/component';

interface StockAdjustmentRow {
  id: string;
  name?: string;
  warehouse_name?: string;
  status?: string;
  note?: string;
  applicant_name?: string;
  created_at?: string;
  [key: string]: unknown;
}

// Define a simple service for admin read-only access
const adminStockAdjustmentService: TableService = {
  getRecords: async (params: Record<string, unknown>) => {
    const response = await adminApiFetch('stock_adjustments/get_records', {
      method: 'POST',
      body: params
    });
    return response.json();
  },
  get: async (id: string) => {
    const response = await adminApiFetch(`stock_adjustments/${id}`);
    return response.json();
  }
};

export default function AdminStockAdjustmentsList() {
  const { t } = useTranslation();

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('models.stock_adjustment.name'),
      component: MultipleValue,
      inputName: 'stock_adjustments.name',
      defaultPinned: true,
      createable: true,
    },
    {
      label: t('models.stock_adjustment.status'),
      component: MultipleValue,
      inputName: 'stock_adjustments.status',
      createable: false,
      defaultOptions: [
        { label: t('enums.stock_adjustment.status.draft'), value: 'draft' },
        { label: t('enums.stock_adjustment.status.pending'), value: 'pending' },
        { label: t('enums.stock_adjustment.status.approved'), value: 'approved' },
        { label: t('enums.stock_adjustment.status.rejected'), value: 'rejected' },
      ],
    },
    {
      label: t('models.stock_adjustment.created_at'),
      component: FullDayRangePicker,
      inputName: 'stock_adjustments.created_at',
    }
  ], [t]);

  const columns = [
    {
      name: t('models.stock_adjustment.name'),
      selector: (row: StockAdjustmentRow) => row.name,
      sortable: true,
      sortFieldName: 'stock_adjustments.name',
      cell: (row: StockAdjustmentRow) => (
        <Link to={`/stock_adjustments/${row.id}`}>{row.name}</Link>
      )
    },
    {
      name: t('models.warehouse.name'),
      selector: (row: StockAdjustmentRow) => row.warehouse_name,
    },
    {
      name: t('models.stock_adjustment.status'),
      selector: (row: StockAdjustmentRow) => row.status,
      cell: (row: StockAdjustmentRow) => {
        let color = 'default';
        if (row.status === 'approved') color = 'success';
        if (row.status === 'rejected') color = 'error';
        if (row.status === 'pending') color = 'processing';
        return <Tag color={color}>{t(`enums.stock_adjustment.status.${row.status}`)}</Tag>;
      }
    },
    {
      name: t('models.stock_adjustment.note'),
      selector: (row: StockAdjustmentRow) => row.note || '-',
    },
    {
      name: t('models.stock_adjustment.applicant'),
      selector: (row: StockAdjustmentRow) => row.applicant_name,
    },
    {
      name: t('models.stock_adjustment.created_at'),
      selector: (row: StockAdjustmentRow) => row.created_at,
      sortable: true,
      sortFieldName: 'stock_adjustments.created_at',
    }
  ];

  return (
    <div>
      <ListPageHeader
        basePath="/admin"
        items={['stocks', 'stocks.adjustments']}
      />
      <TableIndex
        columns={columns}
        tableId="AdminStockAdjustmentsTable"
        service={adminStockAdjustmentService}
        defaultQuery={{
          sorting_field: 'stock_adjustments.created_at',
          sorting_direction: 'desc'
        }}
        supportRules={supportRules}
        searchable={{
          match: 'contains',
          searchFields: [
            t('models.stock_adjustment.name'),
            t('models.stock_adjustment.note'),
          ],
        }}
      />
    </div>
  );
}
